// ─── Flujo piloto, fase 3: la recepción (RP) y el apartado ───────────────────
// Como en el panel de ventas:
//   · Lo que llega se REPARTE: primero al pedido que compró (la OP de la que
//     cuelga la OCP), hasta lo que le falta; luego a los demás pedidos que
//     esperan ese artículo, del más antiguo al más nuevo; lo que sobra queda
//     como inventario libre.
//   · A cada pedido se le APARTA lo que le tocó, sin vencimiento (decisión del
//     usuario). Lo apartado vigente nunca pasa de lo que el pedido todavía
//     tiene por entregar, así que una remisión lo va consumiendo sola y no hay
//     que «descontarlo» a mano.
//   · La factura del proveedor (FP) se cruza contra lo recibido: lo causado de
//     más que lo recibido es una alarma; lo recibido sin causar, un pendiente.
//
// Es puro: los documentos llegan en `ctx` (como en cadenaPedido.js).
import { cadenaDeOP } from "./cadenaPedido.js";

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
  .toLowerCase().replace(/\s+/g, " ").trim();
const num = (v) => { const n = Number(String(v ?? "").replace(/,/g, ".")); return Number.isFinite(n) ? n : 0; };

export const apartadosDeOP = (opId, ctx) => (ctx.apartados || []).filter(a => a.pedido_id === opId && !a.liberado);

// Lo apartado (sin liberar) de un artículo para una OP, sin tope.
export function apartadoTotalFn(ctx) {
  return (op, articulo) => apartadosDeOP(op.id, ctx)
    .filter(a => norm(a.articulo) === norm(articulo))
    .reduce((s, a) => s + num(a.cantidad), 0);
}

// ─── Lo apartado VIGENTE de un artículo, pedido por pedido ──────────────────
// Lo que de verdad está reservado: min(apartado sin liberar, lo que el pedido
// todavía necesita de ese artículo para entregar). Es la misma regla de
// Material en transición, así que la remisión lo consume sola. Solo cuentan
// los pedidos vigentes (ni borrador ni cancelado): el apartado de una orden
// cancelada ya no tiene dueño. Lo usa el flujo de siempre (getStockDisponible)
// para no prometerle a un pedido lo apartado para otro.
// `ctx` necesita `pedidos` y `apartados`, además de lo que pide cadenaDeOP.
export function apartadoVigentePorPedido(articulo, ctx) {
  const k = norm(articulo);
  const porOp = new Map();
  (ctx.apartados || []).filter(a => !a.liberado && norm(a.articulo) === k && num(a.cantidad) > 0)
    .forEach(a => porOp.set(a.pedido_id, (porOp.get(a.pedido_id) || 0) + num(a.cantidad)));
  const out = [];
  porOp.forEach((apartado, opId) => {
    const op = (ctx.pedidos || []).find(o => o.id === opId);
    if (!op || op.estado === "borrador" || op.estado === "cancelada") return;
    const necesita = cadenaDeOP(op, ctx).lineas.reduce((s, l) => s + l.aComprar
      .filter(c => norm(c.articulo) === k).reduce((t, c) => t + c.necesita, 0), 0);
    const cantidad = Math.min(apartado, necesita);
    if (cantidad > 1e-6) out.push({ pedido_id: op.id, pedido_numero: op.numero || "", articulo, cantidad });
  });
  return out;
}

// Lo que cada OP necesita de cada artículo (todas sus filas de transición).
function necesidades(filas) {
  const m = new Map();
  (filas || []).forEach(f => {
    const k = f.cad.op.id + "|" + norm(f.c.articulo);
    const e = m.get(k) || { op: f.cad.op, articulo: f.c.articulo, necesita: 0, apartado: 0, comprado: 0 };
    e.necesita += f.c.necesita;
    e.apartado += f.apartado || 0;
    e.comprado += f.comprado || 0;
    m.set(k, e);
  });
  return m;
}

// ─── El reparto de lo que llegó ─────────────────────────────────────────────
// `items` = [{articulo, cantidad}] lo que se recibe ahora; `filas` las de
// Material en transición ANTES de recibirlo.
export function repartirRecepcion(ocp, items, filas) {
  const origenId = ocp && ocp._origen && ocp._origen.tipo === "OP" ? ocp._origen.id : null;
  const nec = necesidades(filas);
  // Lo que ya se asignó en este mismo reparto (dos líneas del mismo artículo).
  const asignado = new Map();
  const falta = (e, esOrigen) => {
    const k = e.op.id + "|" + norm(e.articulo);
    // Al que compró le falta lo no apartado. A los demás, además, se les
    // descuenta lo que viene en camino por sus propias compras.
    const base = e.necesita - e.apartado - (esOrigen ? 0 : e.comprado);
    return Math.max(0, base - (asignado.get(k) || 0));
  };
  const dar = (e, cant) => { const k = e.op.id + "|" + norm(e.articulo); asignado.set(k, (asignado.get(k) || 0) + cant); };
  const ordenFecha = (a, b) => (a.op.fecha || "").localeCompare(b.op.fecha || "") || String(a.op.numero || "").localeCompare(String(b.op.numero || ""));

  return (items || []).map(it => {
    let resto = num(it.cantidad);
    const destinos = [];
    const candidatos = [...nec.values()].filter(e => norm(e.articulo) === norm(it.articulo));
    const delOrigen = candidatos.filter(e => e.op.id === origenId);
    const otros = candidatos.filter(e => e.op.id !== origenId).sort(ordenFecha);
    for (const [lista, esOrigen] of [[delOrigen, true], [otros, false]]) {
      for (const e of lista) {
        if (resto <= 0) break;
        const c = Math.min(resto, falta(e, esOrigen));
        if (c <= 0) continue;
        dar(e, c);
        resto -= c;
        const ya = destinos.find(d => d.pedido_id === e.op.id);
        if (ya) ya.cantidad += c; else destinos.push({ pedido_id: e.op.id, pedido_numero: e.op.numero, cantidad: c });
      }
    }
    return { ...it, destinos, inventario: resto };
  });
}

// ─── El cruce OC · RP · FP de una orden de compra ───────────────────────────
// `recibible(articulo)`: solo la mercancía que entra a bodega se cruza; una
// línea de servicio (marcación, transporte) nunca se «recibe» y no puede salir
// como causada de más.
export function cruceOCP(ocp, recibible = () => true) {
  const recibidoDe = (art) => ((ocp && ocp._recepciones) || []).reduce((s, r) => s + (r.items || [])
    .filter(it => norm(it.articulo) === norm(art)).reduce((t, it) => t + num(it.cantidad), 0), 0);
  const causadoDe = (art) => ((ocp && ocp._facturasContables) || []).reduce((s, fc) => s + (fc.items || [])
    .filter(it => norm(it.articulo) === norm(art)).reduce((t, it) => t + num(it.cantidad), 0), 0);
  const vistos = new Set();
  return ((ocp && ocp.items) || []).filter(it => recibible(it.articulo))
    .filter(it => { const k = norm(it.articulo); if (vistos.has(k)) return false; vistos.add(k); return true; })
    .map(it => {
      const pedido = ((ocp.items) || []).filter(x => norm(x.articulo) === norm(it.articulo)).reduce((s, x) => s + num(x.cantidad), 0);
      const recibido = recibidoDe(it.articulo), causado = causadoDe(it.articulo);
      const estado = causado > recibido + 1e-6 ? "causado_de_mas" : recibido > causado + 1e-6 ? "por_causar" : "al_dia";
      return { articulo: it.articulo, pedido, recibido, causado, porCausar: Math.max(0, recibido - causado), deMas: Math.max(0, causado - recibido), estado };
    });
}

// Recepciones (RP) que le llevaron material a una OP y que esa OP no ha visto.
export function avisosPendientes(opId, ctx) {
  return (ctx.recepciones || []).filter(rp => (rp.lineas || []).some(l => (l.destinos || []).some(d => d.pedido_id === opId && num(d.cantidad) > 0))
    && !(rp.avisos_vistos || []).includes(opId));
}

// Lo que recibió una OP en una RP, en palabras.
export function loQueLlego(rp, opId) {
  return (rp.lineas || []).flatMap(l => (l.destinos || []).filter(d => d.pedido_id === opId).map(d => ({ articulo: l.articulo, cantidad: num(d.cantidad) })));
}
